import React from "react";
import {
  Eyebrow,
  PrimaryBtn,
  ArrowLink,
  Marquee,
  SectionHead,
} from "../editorial/Layout";

const HomeHero = () => (
  <section className="home-hero">
    <style>{`
      .home-hero { position: relative; padding: 64px 96px 80px; display: grid; grid-template-columns: 1fr 1.05fr; gap: 60px; border-bottom: 1px solid var(--line); }
      .hh-left { display: flex; flex-direction: column; padding-top: 32px; }
      .hh-h1 { font-family: 'Fraunces', serif; font-weight: 300; font-size: 110px; line-height: 0.94; letter-spacing: -0.04em; color: var(--ink); margin: 36px 0 32px; }
      .hh-h1 em { font-style: italic; font-weight: 300; }
      .hh-h1 .clay { color: var(--clay); font-style: italic; font-weight: 300; }
      .hh-lede { font-size: 17px; line-height: 1.55; color: var(--ink-2); max-width: 480px; margin: 0 0 44px; }
      .hh-actions { display: flex; align-items: center; gap: 24px; margin-bottom: 56px; }
      .hh-trust { display: flex; align-items: center; gap: 26px; }
      .hh-av { display: flex; }
      .hh-av span { width: 38px; height: 38px; border-radius: 50%; border: 2px solid var(--bg); background-size: cover; background-position: center; margin-left: -10px; }
      .hh-av span:first-child { margin-left: 0; background: linear-gradient(135deg, #C5A88A, #8B6B4F); }
      .hh-av span:nth-child(2) { background: linear-gradient(135deg, #7E8C8E, #44545B); }
      .hh-av span:nth-child(3) { background: linear-gradient(135deg, #B05A3C, #6E2F1C); }
      .hh-av span:nth-child(4) { background: linear-gradient(135deg, #D9CDB8, #998363); }
      .hh-trust-t { font-size: 13.5px; line-height: 1.45; color: var(--ink-2); }
      .hh-trust-t strong { font-weight: 600; }
      .hh-trust-t .s { color: var(--mute); }

      .hh-right { position: relative; min-height: 700px; }
      .hh-image { position: absolute; inset: 0 0 96px 0; background: url('/assets/hero-kitchen.png') center/cover no-repeat; border-radius: 6px; box-shadow: 0 30px 60px -30px rgba(0,0,0,0.35); }
      .hh-meta { position: absolute; top: 36px; right: 32px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--paper); letter-spacing: 0.14em; text-transform: uppercase; background: rgba(0,0,0,0.4); padding: 8px 12px; backdrop-filter: blur(8px); border-radius: 2px; }
      .hh-cap { position: absolute; left: -52px; bottom: 36px; background: var(--paper); padding: 24px 28px; max-width: 290px; border-radius: 4px; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.18); }
      .hh-cap .num { font-family: 'Fraunces', serif; font-size: 44px; font-weight: 400; letter-spacing: -0.02em; line-height: 1; }
      .hh-cap .num em { font-style: normal; font-size: 22px; color: var(--mute); margin-left: 4px; }
      .hh-cap .lbl { font-size: 12px; color: var(--mute); margin-top: 8px; letter-spacing: 0.04em; }
      .hh-cap .div { height: 1px; background: var(--line); margin: 14px 0; }
      .hh-cap .quote { font-family: 'Fraunces', serif; font-style: italic; font-size: 15.5px; line-height: 1.4; color: var(--ink); }
      .hh-cap .author { font-size: 11px; color: var(--mute); margin-top: 8px; letter-spacing: 0.06em; text-transform: uppercase; }

      @media (max-width: 960px) {
        .home-hero { grid-template-columns: 1fr; padding: 32px 24px 56px; gap: 36px; }
        .hh-h1 { font-size: 56px; }
        .hh-right { min-height: 460px; }
        .hh-image { inset: 0 0 60px 0; }
        .hh-cap { left: 12px; bottom: 12px; }
      }
    `}</style>
    <div className="hh-left">
      <Eyebrow>Interior renovation, Côte d'Azur</Eyebrow>
      <h1 className="hh-h1">
        Renovations,<br />made <em>effortless</em><span className="clay">.</span>
      </h1>
      <p className="hh-lede">
        We pair certified contractors with editorial design judgement — so the
        result is precise, calm, and built to last. Concept to completion in a
        single team.
      </p>
      <div className="hh-actions">
        <PrimaryBtn>Start a project</PrimaryBtn>
        <ArrowLink to="/portofolio">View portfolio</ArrowLink>
      </div>
      <div className="hh-trust">
        <div className="hh-av">
          <span></span><span></span><span></span><span></span>
        </div>
        <div className="hh-trust-t">
          <strong>150+ homes</strong> delivered since 2020<br />
          <span className="s">Average rating 4.9 / 5 · Houzz, Google</span>
        </div>
      </div>
    </div>
    <div className="hh-right">
      <div className="hh-image">
        <div className="hh-meta">Projet Nº 047 · Cannes Croisette</div>
      </div>
      <div className="hh-cap">
        <div className="num">12<em> wks</em></div>
        <div className="lbl">Average kitchen renovation</div>
        <div className="div"></div>
        <div className="quote">"They handled everything — we lived in the result, not the renovation."</div>
        <div className="author">— M. Laurent, owner</div>
      </div>
    </div>
  </section>
);

const HomeFeatured = () => {
  const projects = [
    { n: "01", tag: "Kitchen · Featured", title: "Olive & Oak, Croisette", loc: "Cannes", area: "142 m²", wk: "11 weeks", img: "/assets/hero-kitchen.png", big: true },
    { n: "02", tag: "Bathroom · Statuario", title: "Marble Suite, Mougins", loc: "Mougins", area: "28 m²", wk: "6 weeks", img: "/assets/marmura.jpg" },
    { n: "03", tag: "Living · Oak parquet", title: "Parquet & Light", loc: "Antibes", area: "64 m²", wk: "9 weeks", img: "/assets/parchet.jpg" },
  ];
  return (
    <section className="ed-section">
      <style>{`
        .feat-grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr; gap: 24px; }
        .feat-card { position: relative; overflow: hidden; border-radius: 6px; cursor: pointer; aspect-ratio: 4 / 5; }
        .feat-card.big { aspect-ratio: 4 / 5; }
        .feat-card .img { position: absolute; inset: 0; background-size: cover; background-position: center; transition: transform .5s ease; }
        .feat-card:hover .img { transform: scale(1.04); }
        .feat-card .scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%); }
        .feat-card .num { position: absolute; top: 18px; left: 20px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--paper); letter-spacing: 0.16em; }
        .feat-card .tag { position: absolute; top: 18px; right: 20px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--paper); background: rgba(0,0,0,0.4); padding: 6px 10px; backdrop-filter: blur(6px); border-radius: 2px; letter-spacing: 0.12em; text-transform: uppercase; }
        .feat-card .meta { position: absolute; left: 20px; right: 20px; bottom: 22px; color: var(--paper); }
        .feat-card .title { font-family: 'Fraunces', serif; font-weight: 400; font-size: 28px; line-height: 1.08; letter-spacing: -0.02em; margin: 0 0 6px; }
        .feat-card.big .title { font-size: 40px; }
        .feat-card .sub { font-size: 13px; color: rgba(251,248,242,0.85); }
        .feat-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 48px; padding-top: 28px; border-top: 1px solid var(--line); }
        .feat-foot .l { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.14em; text-transform: uppercase; }
        @media (max-width: 960px) { .feat-grid { grid-template-columns: 1fr; } }
      `}</style>
      <SectionHead
        num="01"
        eyebrow="Selected projects"
        title='Quiet renovations,<br/><em>built to last</em>.'
        right="Six years of interior renovations across Cannes, Mougins, Antibes and the Côte d'Azur — kitchens, baths, living rooms, and the occasional terrace."
      />
      <div className="feat-grid">
        {projects.map((p) => (
          <div key={p.n} className={`feat-card ${p.big ? "big" : ""}`}>
            <div className="img" style={{ backgroundImage: `url(${p.img})` }}></div>
            <div className="scrim"></div>
            <div className="num">{p.n}</div>
            <div className="tag">{p.tag}</div>
            <div className="meta">
              <h3 className="title">{p.title}</h3>
              <div className="sub">{p.loc} · {p.area} · {p.wk}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="feat-foot">
        <span className="l">152 projects · 6 years · Cannes &amp; Côte d'Azur</span>
        <ArrowLink to="/portofolio">Browse the full portfolio</ArrowLink>
      </div>
    </section>
  );
};

const HomeServices = () => {
  const services = [
    { n: "01", t: "Plastering", d: "Walls and false ceilings in classic, acoustic or water-repellent plasterboard. Thermal and acoustic insulation built in." },
    { n: "02", t: "Electrical", d: "Switchboards, built-in or visible circuits, equipment installation, switches, and computer network." },
    { n: "03", t: "Plumbing", d: "New bathrooms and shower rooms, walk-in showers, WC and bath replacement, water heater installation." },
    { n: "04", t: "Tiling & marble", d: "Floor tiles, wall tiles, marble or mosaic for indoor or outdoor use — every size, every finish." },
    { n: "05", t: "Painting", d: "Painters trained in modern application techniques. Matt, satin, gloss, lime wash, decorative finishes." },
    { n: "06", t: "False ceilings", d: "Sloped or straight, on rails or hangers — plasterboard, acoustic or water-repellent, with insulation if needed." },
    { n: "07", t: "XTone & Fitwall", d: "Large-format porcelain and high-resistance panels for surfaces that look like marble, wood, terracotta or textile." },
    { n: "08", t: "Air, alum. & PVC", d: "Air conditioning, aluminium and PVC joinery — installation, replacement, and maintenance." },
  ];
  return (
    <section className="ed-section">
      <style>{`
        .srv-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 1px solid var(--line); border-left: 1px solid var(--line); }
        .srv-cell { padding: 32px 28px 36px; border-right: 1px solid var(--line); border-bottom: 1px solid var(--line); min-height: 240px; display: flex; flex-direction: column; justify-content: space-between; cursor: pointer; transition: background .25s; }
        .srv-cell:hover { background: var(--paper); }
        .srv-cell .n { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.16em; }
        .srv-cell h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 30px; line-height: 1.05; letter-spacing: -0.02em; margin: 22px 0 12px; }
        .srv-cell p { font-size: 13.5px; line-height: 1.55; color: var(--ink-2); margin: 0; }
        .srv-cell .arr { margin-top: 18px; display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: var(--ink); border-bottom: 1px solid transparent; transition: border .2s; align-self: flex-start; }
        .srv-cell:hover .arr { border-bottom-color: var(--ink); }
        @media (max-width: 960px) { .srv-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>
      <SectionHead
        num="02"
        eyebrow="What we do"
        title='A single team,<br/>from <em>brief to finish</em>.'
        right="Concept, drawings, contractors, finish — everything that touches your renovation goes through one project lead. No relay, no surprises."
      />
      <div className="srv-grid">
        {services.map((s) => (
          <div key={s.n} className="srv-cell">
            <div>
              <span className="n">{s.n}</span>
              <h3>{s.t}</h3>
              <p>{s.d}</p>
            </div>
            <span className="arr">Read more →</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const HomeProcess = () => {
  const steps = [
    { n: "01", t: "First conversation", d: "A free 45-minute call. We listen, look at your space (in person if you're in Cannes), and tell you what is and isn't realistic." },
    { n: "02", t: "Brief & estimate", d: "A written brief, a fixed-line estimate, and a calendar. No padded line items, no hidden contractor mark-ups." },
    { n: "03", t: "Concept & drawings", d: "Plans, finishes, material samples. We iterate until you can picture the result before a hammer touches the wall." },
    { n: "04", t: "Build & finish", d: "One project lead, one clean site, one schedule. Weekly updates, fixed delivery date, full warranty on every trade." },
  ];
  return (
    <section className="ed-section">
      <style>{`
        .proc-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; }
        .proc-step { padding-top: 28px; border-top: 1px solid var(--ink); position: relative; }
        .proc-step .n { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.16em; }
        .proc-step h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 32px; line-height: 1.05; letter-spacing: -0.02em; margin: 18px 0 14px; }
        .proc-step p { font-size: 14px; line-height: 1.55; color: var(--ink-2); margin: 0; }
        .proc-tag { display: inline-flex; align-items: center; gap: 8px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--clay); letter-spacing: 0.16em; text-transform: uppercase; padding: 5px 0; margin-top: 24px; }
        @media (max-width: 960px) { .proc-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>
      <SectionHead
        num="03"
        eyebrow="How we work"
        title='Four conversations,<br/>one <em>finished home</em>.'
        right="Our process is short on purpose. We've cut the meetings, the relay between trades, and the line-item surprises. You'll hear from us when something matters."
      />
      <div className="proc-grid">
        {steps.map((s) => (
          <div key={s.n} className="proc-step">
            <span className="n">{s.n}</span>
            <h3>{s.t}</h3>
            <p>{s.d}</p>
            <div className="proc-tag">✦ Avg. 5–9 days</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const HomeValues = () => {
  const values = [
    { t: "Meticulous work, clean site", d: "Your home stays liveable. We protect, we cover, we clean every evening — and you don't inherit a worksite." },
    { t: "Rapid intervention", d: "Project leads on the phone, not in voicemail. Same-day reply during your build, fixed weekly check-ins." },
    { t: "Wise advice", d: "We will tell you which finish ages well, which one doesn't, and where to spend or save. Even the parts that lose us margin." },
    { t: "Competitive prices", d: "No mark-up on contractors. Transparent estimates with line-item costs you can compare. Quotes valid for 30 days." },
  ];
  return (
    <section className="ed-section" style={{ background: "var(--paper)" }}>
      <style>{`
        .val-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 80px; }
        .val-row { display: grid; grid-template-columns: 38px 1fr; gap: 24px; padding: 32px 0; border-top: 1px solid var(--line); }
        .val-row .ico { width: 38px; height: 38px; border-radius: 50%; border: 1px solid var(--ink); display: flex; align-items: center; justify-content: center; font-family: 'Fraunces', serif; font-style: italic; font-size: 22px; }
        .val-row h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 28px; letter-spacing: -0.02em; margin: 0 0 8px; }
        .val-row p { font-size: 14.5px; line-height: 1.55; color: var(--ink-2); margin: 0; }
        @media (max-width: 960px) { .val-grid { grid-template-columns: 1fr; } }
      `}</style>
      <SectionHead
        num="04"
        eyebrow="What we promise"
        title='CitiBatiment <em>assures you</em>.'
        right="Four promises, six years of keeping them. If we ever drop one of these on your project, the rework is on us."
      />
      <div className="val-grid">
        {values.map((v, i) => (
          <div key={v.t} className="val-row">
            <span className="ico">{["✦", "◐", "◇", "✕"][i]}</span>
            <div>
              <h3>{v.t}</h3>
              <p>{v.d}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const HomeReviews = () => {
  const reviews = [
    { name: "Isabelle Desmarets", date: "02 / 2020", loc: "Cannes", text: "Réactif, professionnels — les deux mots à retenir. Un chantier propre, efficace et organisé. Cela fait vraiment plaisir." },
    { name: "Ofi Skenazene", date: "08 / 2023", loc: "Mougins", text: "Très satisfait de leur travail, très ponctuels, très gentils, un travail très propre. Nous recommandons fortement." },
    { name: "Marina Guzzo", date: "07 / 2021", loc: "Antibes", text: "Super travail, très professionnel, propre, rapide et à l'écoute ! Je recommande à 100% pour tous types de travaux." },
  ];
  return (
    <section className="ed-section">
      <style>{`
        .rev-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .rev-card { background: var(--paper); border-radius: 6px; padding: 32px 30px; display: flex; flex-direction: column; min-height: 280px; }
        .rev-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
        .rev-stars { color: var(--clay); font-size: 16px; letter-spacing: 4px; }
        .rev-date { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.12em; }
        .rev-text { font-family: 'Fraunces', serif; font-weight: 300; font-size: 22px; line-height: 1.35; letter-spacing: -0.01em; flex: 1; }
        .rev-text::before { content: '"'; font-family: 'Fraunces', serif; font-style: italic; font-size: 48px; color: var(--clay); line-height: 0; vertical-align: -12px; margin-right: 6px; }
        .rev-foot { margin-top: 26px; padding-top: 18px; border-top: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; font-size: 13px; }
        .rev-foot .name { font-weight: 600; color: var(--ink); }
        .rev-foot .loc { color: var(--mute); }
        @media (max-width: 960px) { .rev-grid { grid-template-columns: 1fr; } }
      `}</style>
      <SectionHead
        num="05"
        eyebrow="Reviews"
        title='In their <em>own words</em>.'
        right="4.9 / 5 average across Google and Houzz, from 80+ reviewed projects. The full archive is in the journal."
      />
      <div className="rev-grid">
        {reviews.map((r) => (
          <div key={r.name} className="rev-card">
            <div className="rev-head">
              <span className="rev-stars">★★★★★</span>
              <span className="rev-date">{r.date}</span>
            </div>
            <p className="rev-text">{r.text}</p>
            <div className="rev-foot">
              <span className="name">{r.name}</span>
              <span className="loc">{r.loc}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Home() {
  return (
    <main className="page-enter">
      <HomeHero />
      <Marquee items={["Kitchens", "Bathrooms", "Plastering", "Tiling & marble", "Electrical", "Painting", "False ceilings", "XTone & Fitwall", "Aluminium · PVC", "Air conditioning"]} />
      <HomeFeatured />
      <HomeServices />
      <HomeProcess />
      <HomeValues />
      <HomeReviews />
    </main>
  );
}

export default Home;
